// Pure rules for the admin Payment requests screen: money parsing and
// formatting, the default Feis.link fee, form validation and status labels.
// The server re-validates everything (backend helpers/paymentRequests.js).

export const DEFAULT_FEE_PERCENT = 6;
export const NOTE_MAX = 500;
export const MIN_ORGANIZER_CENTS = 100;
export const MAX_ORGANIZER_CENTS = 10000000;

/** "$1,234.5" / "50" / "50.00" -> cents, or null when it is not an amount. */
export function parseDollarsToCents(value) {
  const s = String(value ?? '').trim().replace(/^\$/, '').replace(/,/g, '').trim();
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return null;
  const [whole, frac = ''] = s.split('.');
  return Number(whole) * 100 + Number((frac + '00').slice(0, 2));
}

/** cents -> "50.00" for an input field. */
export const centsToInput = (cents) => (Number.isFinite(cents) ? (cents / 100).toFixed(2) : '');

/**
 * The event's own service-fee rule (sent by the backend with each event:
 * helpers/paymentRequests.js feeRuleFor). Oireachtas events charge a
 * percentage; feis and grade events the standard tiers. No event yet: 6%.
 */
export const FEIS_STANDARD_LABEL = '$10 up to $100 · 10% up to $200 · $20 above';

const percentRule = (pct) => ({ kind: 'percent', percent: pct, label: `${pct}% of the organizer amount` });

export function feeRuleOf(event) {
  const r = event?.feeRule;
  if (r && (r.kind === 'feis_standard' || (r.kind === 'percent' && Number.isFinite(Number(r.percent))))) return r;
  // No (or unusable) feeRule: derive it from the documented event fields, the
  // same way the backend's feeRuleFor does.
  if (event?.type === 'feis' || event?.type === 'grade') return { kind: 'feis_standard', label: FEIS_STANDARD_LABEL };
  if (event?.type === 'oireachtas') {
    const pct = event.feePercent;
    return percentRule(typeof pct === 'number' && Number.isFinite(pct) ? pct : DEFAULT_FEE_PERCENT);
  }
  return percentRule(DEFAULT_FEE_PERCENT);
}

/**
 * Standard feis/grade service fee — a copy of the backend's
 * helpers/feisPricing.js standardServiceFeeCents (up to $100: $10; up to
 * $200: 10% rounded to the dollar; above: $20). The test pins the same values
 * as the backend's test.
 */
export function standardServiceFeeCents(amountCents) {
  if (amountCents <= 10000) return 1000;
  if (amountCents <= 20000) return Math.round(amountCents / 100 / 10) * 100;
  return 2000;
}

/** Default Feis.link fee for an organizer amount under the event's rule. */
export function defaultFeeCents(organizerCents, rule = feeRuleOf(null)) {
  if (!Number.isFinite(organizerCents) || organizerCents <= 0) return 0;
  if (rule.kind === 'feis_standard') return standardServiceFeeCents(organizerCents);
  return Math.round((organizerCents * Number(rule.percent)) / 100);
}

/** Money in the request's currency, Canadian formatting. */
export function money(cents, currency) {
  if (cents === null || cents === undefined || !Number.isFinite(Number(cents))) return '—';
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: String(currency || 'cad').toUpperCase(),
  }).format(Number(cents) / 100);
}

export const day = (iso) =>
  iso ? new Date(iso).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' }) : '';

export const when = (iso) =>
  iso
    ? new Date(iso).toLocaleString('en-CA', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '';

/** A calendar day stored as "YYYY-MM-DD" (or an ISO midnight UTC) shown without a time-zone shift. */
export const dueDay = (value) => {
  if (!value) return '';
  const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return day(value);
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString('en-CA', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

export const EVENT_TYPE_LABELS = { feis: 'Feis', grade: 'Grade exam', oireachtas: 'Oireachtas' };

export const ATTENTION_LABELS = {
  receipt_not_sent: 'Receipt not sent',
  duplicate_payment: 'Paid twice',
  paid_after_cancel: 'Paid after cancel',
  amount_mismatch: 'Amount mismatch',
};

export const STATUS_FILTERS = [
  { key: '', label: 'All', count: 'all' },
  { key: 'open', label: 'Open', count: 'open' },
  { key: 'paid', label: 'Paid', count: 'paid' },
  { key: 'canceled', label: 'Cancelled', count: 'canceled' },
  { key: 'attention', label: 'Needs attention', count: 'attention', warn: true },
];

/** Pill text + .op-pill modifier for a row. Attention wins over the status. */
export function statusPill(row) {
  if (row?.attention) return { label: ATTENTION_LABELS[row.attention] || 'Needs attention', cls: 'closed' };
  if (row?.status === 'paid') return { label: row.paidAt ? `Paid ${day(row.paidAt).replace(/, \d{4}$/, '')}` : 'Paid', cls: 'open' };
  if (row?.status === 'canceled') return { label: 'Cancelled', cls: '' };
  return { label: 'Open', cls: 'late' };
}

export const emptyForm = () => ({
  organizer: null,
  eventKey: '',
  payer: null,
  amount: '',
  fee: '',
  feeTouched: false,
  note: '',
  dueAt: '',
});

export const eventKeyOf = (e) => (e ? `${e.type}:${e.id}` : '');

/**
 * Validate the New request form. Returns { errors } (empty object = valid)
 * and the payload for POST /payment-requests/admin when valid.
 */
export function validateForm(form, events = []) {
  const errors = {};
  const event = events.find((e) => eventKeyOf(e) === form.eventKey);
  if (!form.organizer) errors.organizer = 'Choose the organizer.';
  if (!event) errors.event = 'Choose the event.';
  else if (!event.account?.assigned) errors.event = 'This event has no payout account. Add one before requesting a payment.';
  if (!form.payer) errors.payer = 'Choose who pays.';
  const organizerAmountCents = parseDollarsToCents(form.amount);
  if (organizerAmountCents === null) errors.amount = 'Enter an amount, for example 50.00.';
  else if (organizerAmountCents < MIN_ORGANIZER_CENTS) errors.amount = 'The amount must be at least $1.00.';
  else if (organizerAmountCents > MAX_ORGANIZER_CENTS) errors.amount = 'The amount must be $100,000.00 or less.';
  const feeCents = parseDollarsToCents(form.fee === '' ? '0' : form.fee);
  if (feeCents === null) errors.fee = 'Enter the fee, for example 3.00 (or 0).';
  else if (organizerAmountCents !== null && feeCents >= organizerAmountCents) errors.fee = 'The fee must be less than the organizer amount.';
  const note = String(form.note || '').trim();
  if (!note) errors.note = 'Say what the payment is for. The payer sees this.';
  else if (note.length > NOTE_MAX) errors.note = `Keep it to ${NOTE_MAX} characters.`;
  if (form.dueAt && !/^\d{4}-\d{2}-\d{2}$/.test(form.dueAt)) errors.dueAt = 'Use a date like 2026-10-16.';
  if (Object.keys(errors).length) return { errors };
  return {
    errors,
    payload: {
      event_type: event.type,
      event_id: event.id,
      payer_id: form.payer.id,
      organizerAmountCents,
      feeCents,
      note,
      dueAt: form.dueAt || null,
    },
  };
}

/** Totals shown live in the dialog (null pieces while the inputs are incomplete). */
export function previewTotals(form) {
  const organizerCents = parseDollarsToCents(form.amount);
  const feeCents = parseDollarsToCents(form.fee === '' ? '0' : form.fee);
  return {
    organizerCents,
    feeCents,
    totalCents: organizerCents !== null && feeCents !== null ? organizerCents + feeCents : null,
  };
}

export const errText = (e) => {
  const m = e?.messages ?? e?.message;
  if (Array.isArray(m)) return m.join(' ');
  return m || 'Something went wrong. Please try again.';
};
